import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Appointment } from "~/features/appointments/types";
import { Response } from "~/features/dashboard/api/type";
import { apiClient } from "~/lib/client";
import { handleApiError, handleResponse } from "~/utils/response";
import { quickActionMutationKeys } from "./keys";
import { toast } from "sonner";
import { quickActionsKeys } from "../queries/keys";
import { encountersKeys } from "../../../encounters/api/queries";


type UpdateAppointmentOptions = {
    id: string
}


type UpdateAppointmentPayload = {
    status: string
}

type UpdateAppointmentDetailsResponse = Response & {
    appointment: Appointment
}


export const useUpdateAppointmentMutation = (opts: UpdateAppointmentOptions) => {
    const queryClient = useQueryClient();
    return useMutation<UpdateAppointmentDetailsResponse, unknown, UpdateAppointmentPayload>({
        mutationKey: quickActionMutationKeys.updateAppointmentsById(opts.id),
        mutationFn: async (variables: UpdateAppointmentPayload) => {
            try {
                const response = await apiClient(`/admin/appointments/${opts.id}`, {
                    method: "PUT",
                    body: JSON.stringify({
                        status: variables.status
                    }),
                });

                const result = await handleResponse<UpdateAppointmentDetailsResponse>(response);

                if (result.success) {
                    return result.data;
                } else {
                    throw new Error(result.error || 'Failed to update appointment');
                }
            } catch (error) {
                throw handleApiError(error);
            }
        },
        onSuccess: () => {
            toast.success("Appointment status updated successfully!");
            queryClient.invalidateQueries({ queryKey: quickActionsKeys.appointmentById(opts.id) });
            queryClient.invalidateQueries({ queryKey: quickActionsKeys.all });
            queryClient.invalidateQueries({ queryKey: encountersKeys.all });
        }
    })
}